import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowRight, ArrowLeft, Crown, Check, Loader2, Mail, Phone, User,
  Truck, Target, TrendingUp, Shield, Sparkles, AlertCircle,
} from 'lucide-react';
import SEO from '../components/shared/SEO';
import { HexAccent, HexPattern } from '../components/hive/HexMotif';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGES = [
  {
    value: 'build',
    label: 'I want to start',
    sub: 'I need help setting up my DOT/MC Authority',
    package: 'Build',
    color: '#F59E0B',
    Icon: Truck,
  },
  {
    value: 'protect',
    label: 'I just got my authority',
    sub: 'I need help with compliance / marketing',
    package: 'Protect',
    color: '#3B82F6',
    Icon: Shield,
  },
  {
    value: 'grow',
    label: 'I am an established carrier',
    sub: 'I need premium revenue dispatch',
    package: 'Grow',
    color: '#10B981',
    Icon: TrendingUp,
  },
];

const TRUCKS = [
  { value: '1', label: '1 Truck (Owner-Operator)' },
  { value: '2-5', label: '2–5 Trucks (Small Fleet)' },
  { value: '5+', label: '5+ Trucks' },
];

const BOTTLENECKS = [
  { value: 'compliance', label: 'Navigating DOT regulations and paperwork' },
  { value: 'lanes', label: 'Finding high-paying freight / consistent lanes' },
  { value: 'ifta_audits', label: 'Keeping up with IFTA, audits, and compliance' },
];

const IntakeFormPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submissionId, setSubmissionId] = useState(null);
  const [recommendedPackage, setRecommendedPackage] = useState(null);
  const [error, setError] = useState('');

  const [step1, setStep1] = useState({ fullName: '', email: '', phone: '' });
  const [step2, setStep2] = useState({ stage: '', trucks: '', bottleneck: '', longTerm: '' });

  const refCode = new URLSearchParams(window.location.search).get('ref');

  const submitStep1 = async (e) => {
    e?.preventDefault();
    setError('');
    if (!step1.fullName.trim() || !step1.email.trim() || !step1.phone.trim()) {
      setError('Please complete all fields.');
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/intake/step-1`, {
        full_name: step1.fullName,
        email: step1.email,
        phone: step1.phone,
        ref_code: refCode,
      });
      setSubmissionId(r.data.submission_id);
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const submitStep2 = async (e) => {
    e?.preventDefault();
    setError('');
    if (!step2.stage || !step2.trucks || !step2.bottleneck || !step2.longTerm) {
      setError('Please answer all questions.');
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/intake/step-2`, {
        submission_id: submissionId,
        stage: step2.stage,
        trucks: step2.trucks,
        bottleneck: step2.bottleneck,
        long_term_partner: step2.longTerm,
      });
      setRecommendedPackage(r.data.recommended_package);
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] relative">
      <SEO
        title="Request Access to The Hive | Queen B Logistics"
        description="Apply to join the most intentional dispatch network in freight. Two quick steps."
      />
      <HexPattern opacity={0.025} />

      <div className="relative max-w-2xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#D4AF37]/10 border border-[#D4AF37]/30 mb-4">
            <Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" />
            <span className="text-[#D4AF37] text-[10px] font-bold uppercase tracking-[0.25em]">
              Request Access · Serious Operators Only
            </span>
          </div>
          <Crown className="w-12 h-12 text-[#D4AF37] mx-auto mb-3" />
          <h1 className="font-['Barlow_Condensed'] text-3xl sm:text-5xl font-black text-white uppercase tracking-tight leading-none mb-3">
            Join <span className="text-[#D4AF37]">The Hive</span>
          </h1>
          <p className="text-white/60 text-sm sm:text-base max-w-md mx-auto">
            Two quick steps. Approved applicants receive an exclusive link to set up access within 2–5 minutes.
          </p>
        </div>

        {/* Step indicator */}
        {step < 3 && (
          <div className="flex items-center justify-center gap-2 mb-8">
            <StepDot active={step >= 1} done={step > 1} number={1} label="Basics" />
            <div className={`h-px w-12 ${step >= 2 ? 'bg-[#D4AF37]' : 'bg-white/10'}`} />
            <StepDot active={step >= 2} done={step > 2} number={2} label="Goals" />
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/40 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-red-300 text-sm">{error}</p>
          </div>
        )}

        {/* ───────── Step 1 ───────── */}
        {step === 1 && (
          <form onSubmit={submitStep1} className="bg-[#121212] border border-white/10 p-5 sm:p-8 space-y-5" data-testid="intake-step-1">
            <div>
              <h2 className="font-['Barlow_Condensed'] text-2xl font-bold text-white uppercase tracking-wide mb-1">
                Step 1: The Basics
              </h2>
              <p className="text-white/50 text-sm">Contact info — quick & painless.</p>
            </div>

            <Field
              label="Full Name"
              icon={User}
              value={step1.fullName}
              onChange={(v) => setStep1({ ...step1, fullName: v })}
              placeholder="First & Last Name"
              testid="intake-name"
              required
            />
            <Field
              label="Email Address"
              icon={Mail}
              type="email"
              value={step1.email}
              onChange={(v) => setStep1({ ...step1, email: v })}
              placeholder="example@email.com"
              testid="intake-email"
              required
            />
            <Field
              label="Phone Number"
              icon={Phone}
              type="tel"
              value={step1.phone}
              onChange={(v) => setStep1({ ...step1, phone: v })}
              placeholder="(555) 555-5555"
              testid="intake-phone"
              required
            />

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-4 bg-[#D4AF37] text-black font-black uppercase tracking-wider hover:bg-[#F5D47B] disabled:opacity-40 transition-colors flex items-center justify-center gap-2"
              data-testid="intake-step-1-next"
            >
              {submitting ? <><Loader2 className="w-5 h-5 animate-spin" /> Continuing...</> : <>Continue <ArrowRight className="w-5 h-5" /></>}
            </button>

            <p className="text-white/30 text-xs text-center">
              Already in the Hive? <button type="button" onClick={() => navigate('/hive/login')} className="text-[#D4AF37] hover:text-[#F5D47B]">Sign in</button>
            </p>
          </form>
        )}

        {/* ───────── Step 2 ───────── */}
        {step === 2 && (
          <form onSubmit={submitStep2} className="bg-[#121212] border border-white/10 p-5 sm:p-8 space-y-6" data-testid="intake-step-2">
            <div>
              <h2 className="font-['Barlow_Condensed'] text-2xl font-bold text-white uppercase tracking-wide mb-1">
                Step 2: Business Goals
              </h2>
              <p className="text-white/50 text-sm">Helps us route you to the right Hive package.</p>
            </div>

            {/* Stage */}
            <QuestionGroup label="What stage is your trucking business currently in?">
              {STAGES.map((s) => (
                <SelectableCard
                  key={s.value}
                  selected={step2.stage === s.value}
                  onClick={() => setStep2({ ...step2, stage: s.value })}
                  color={s.color}
                  testid={`stage-${s.value}`}
                >
                  <div className="flex items-start gap-3">
                    <s.Icon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: s.color }} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <p className="text-white font-medium text-sm">{s.label}</p>
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5"
                          style={{ color: s.color, backgroundColor: `${s.color}15`, border: `1px solid ${s.color}40` }}>
                          → {s.package}
                        </span>
                      </div>
                      <p className="text-white/50 text-xs mt-0.5">{s.sub}</p>
                    </div>
                  </div>
                </SelectableCard>
              ))}
            </QuestionGroup>

            {/* Trucks */}
            <QuestionGroup label="How many trucks do you operate or plan to launch with?">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {TRUCKS.map((t) => (
                  <SelectableCard
                    key={t.value}
                    selected={step2.trucks === t.value}
                    onClick={() => setStep2({ ...step2, trucks: t.value })}
                    color="#D4AF37"
                    testid={`trucks-${t.value}`}
                    compact
                  >
                    <p className="text-white text-sm text-center">{t.label}</p>
                  </SelectableCard>
                ))}
              </div>
            </QuestionGroup>

            {/* Bottleneck */}
            <QuestionGroup label="What is your single biggest business bottleneck right now?">
              {BOTTLENECKS.map((b) => (
                <SelectableCard
                  key={b.value}
                  selected={step2.bottleneck === b.value}
                  onClick={() => setStep2({ ...step2, bottleneck: b.value })}
                  color="#D4AF37"
                  testid={`bottleneck-${b.value}`}
                  compact
                >
                  <p className="text-white text-sm">{b.label}</p>
                </SelectableCard>
              ))}
            </QuestionGroup>

            {/* Long term */}
            <QuestionGroup label="Are you looking for a dedicated, long-term operational partner to scale with?">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <SelectableCard
                  selected={step2.longTerm === 'yes'}
                  onClick={() => setStep2({ ...step2, longTerm: 'yes' })}
                  color="#10B981"
                  testid="longterm-yes"
                  compact
                >
                  <p className="text-white text-sm text-center">Yes, I am serious about growing.</p>
                </SelectableCard>
                <SelectableCard
                  selected={step2.longTerm === 'no'}
                  onClick={() => setStep2({ ...step2, longTerm: 'no' })}
                  color="#888"
                  testid="longterm-no"
                  compact
                >
                  <p className="text-white text-sm text-center">No, just looking for temporary information.</p>
                </SelectableCard>
              </div>
            </QuestionGroup>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="px-5 py-3 border border-white/20 text-white hover:bg-white/5 flex items-center gap-2 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 py-3 bg-[#D4AF37] text-black font-black uppercase tracking-wider hover:bg-[#F5D47B] disabled:opacity-40 transition-colors flex items-center justify-center gap-2"
                data-testid="intake-submit-btn"
              >
                {submitting ? <><Loader2 className="w-5 h-5 animate-spin" /> Submitting...</> : <>Request Access to the Hive <ArrowRight className="w-5 h-5" /></>}
              </button>
            </div>
          </form>
        )}

        {/* ───────── Success ───────── */}
        {step === 3 && (
          <div className="bg-gradient-to-br from-[#121212] to-[#0A0A0A] border-2 border-[#D4AF37]/40 p-6 sm:p-10 text-center relative overflow-hidden" data-testid="intake-success">
            <HexAccent size={100} color="#D4AF37" className="absolute -top-6 -left-6 opacity-10" />
            <HexAccent size={80} color="#D4AF37" className="absolute -bottom-6 -right-6 opacity-10" />

            <div className="relative">
              <div className="w-20 h-20 bg-green-500/10 border-2 border-green-500 rounded-full flex items-center justify-center mx-auto mb-5">
                <Check className="w-10 h-10 text-green-500" strokeWidth={3} />
              </div>

              <p className="text-[#D4AF37] text-xs font-bold uppercase tracking-[0.3em] mb-3">
                Application Received
              </p>
              <h2 className="font-['Barlow_Condensed'] text-3xl sm:text-4xl font-black text-white uppercase tracking-wide mb-4">
                Welcome to The Next Level
              </h2>

              <p className="text-white/70 text-sm sm:text-base leading-relaxed mb-6 max-w-lg mx-auto">
                Our team is reviewing your intake to ensure Queen B Logistics is the right fit for your business goals.
              </p>

              {recommendedPackage && (
                <div className="bg-black/40 border border-[#D4AF37]/30 p-4 mb-6 max-w-md mx-auto">
                  <p className="text-white/50 text-xs uppercase tracking-wider mb-1">Recommended path</p>
                  <p className="text-[#D4AF37] font-['Barlow_Condensed'] text-2xl font-black uppercase">
                    {recommendedPackage}
                  </p>
                </div>
              )}

              <div className="bg-white/5 border-l-2 border-[#D4AF37] p-4 text-left max-w-lg mx-auto mb-6">
                <p className="text-white font-bold uppercase tracking-wider text-xs mb-2">What happens next</p>
                <p className="text-white/70 text-sm leading-relaxed">
                  Check your inbox in <strong className="text-[#D4AF37]">2–5 minutes</strong>. If approved, we'll send you an exclusive link to access your <strong className="text-white">Queen B Client Portal</strong> and complete your official setup.
                </p>
              </div>

              <button
                onClick={() => navigate('/')}
                className="px-6 py-3 border border-white/20 text-white hover:bg-white/5 transition-colors uppercase tracking-wider text-sm"
              >
                Return Home
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const StepDot = ({ active, done, number, label }) => (
  <div className="flex flex-col items-center gap-1">
    <div className={`w-8 h-8 flex items-center justify-center text-xs font-bold border-2 transition-colors ${
      done ? 'bg-[#D4AF37] border-[#D4AF37] text-black'
        : active ? 'bg-black border-[#D4AF37] text-[#D4AF37]'
        : 'bg-black border-white/20 text-white/40'
    }`}
      style={{ clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)' }}>
      {done ? <Check className="w-3.5 h-3.5" strokeWidth={3} /> : number}
    </div>
    <span className={`text-[10px] uppercase tracking-wider ${active ? 'text-[#D4AF37]' : 'text-white/40'}`}>{label}</span>
  </div>
);

const Field = ({ label, icon: Icon, type = 'text', value, onChange, placeholder, testid, required }) => (
  <div>
    <label className="text-white/70 text-xs uppercase tracking-wider mb-1.5 block font-bold">{label}</label>
    <div className="relative">
      <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className="w-full pl-10 pr-3 py-3 bg-black/40 border border-white/10 text-white focus:border-[#D4AF37] focus:outline-none text-sm"
        data-testid={testid}
      />
    </div>
  </div>
);

const QuestionGroup = ({ label, children }) => (
  <div>
    <p className="text-white/80 text-sm font-bold mb-3">{label}</p>
    <div className="space-y-2">{children}</div>
  </div>
);

const SelectableCard = ({ selected, onClick, children, color, testid, compact }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full ${compact ? 'p-3' : 'p-4'} border-2 transition-all text-left ${
      selected ? 'bg-black/60' : 'bg-black/30 border-white/10 hover:border-white/30'
    }`}
    style={selected ? { borderColor: color, boxShadow: `0 0 0 1px ${color}30` } : {}}
    data-testid={testid}
  >
    {children}
  </button>
);

export default IntakeFormPage;
